/**
 * Shared types for command-palette **entity** hits (W129-F5-S2).
 *
 * Static actions live in `../types.ts` (`CommandAction`) and are registered
 * up-front via the singleton `actionRegistry`. Entity hits are dynamic:
 * they're computed live in the palette body from cached TanStack Query
 * results filtered by the current input, so they need React hooks rather
 * than a registry-side write.
 *
 * The two shapes intentionally don't share an interface — actions have
 * `run`/`permissions`/`group` semantics that don't apply to entity hits
 * (which always navigate, are always visible to the requester since the
 * underlying list query already RBAC-filters, and group by entity type).
 */
import type { ComponentType } from 'react';

export type CommandEntityKind = 'buildings' | 'portfolios' | 'evaluations';

export interface CommandEntityHit {
  /** Stable key for React lists + `cmdk` value-matching. */
  readonly id: string;
  /** Display label shown in the row. */
  readonly label: string;
  /** Secondary line — partner name, address, etc. Optional. */
  readonly sublabel?: string;
  /** Which entity-kind group this hit belongs to. */
  readonly kind: CommandEntityKind;
  /** Icon glyph rendered on the left. */
  readonly icon?: ComponentType<{ className?: string }>;
  /** Fires when the user selects the row. Navigates to the entity detail. */
  readonly onSelect: () => void;
}

/** How many results to show per entity group. */
export const MAX_HITS_PER_GROUP = 5;
